import React, { useEffect } from 'react';
import '../styles/pages/Privacy.scss';
import { Helmet } from 'react-helmet';

const Privacy = () => {
  // Set page title
  useEffect(() => {
    document.title = "Privacy & Cookies Policy - FirstWhistle";
  }, []);

  return (
    <div className="privacy-page">
      <Helmet>
        <title>Privacy & Cookies Policy | FirstWhistle</title>
        <meta name="description" content="FirstWhistle's Privacy and Cookie Policy explains how we collect, use, and protect your personal information when you use our football team management application." />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://firstwhistle.app/privacy" />
        <meta property="og:title" content="Privacy & Cookies Policy | FirstWhistle" />
        <meta property="og:description" content="FirstWhistle's Privacy and Cookie Policy explains how we collect, use, and protect your personal information when you use our football team management application." />
        <meta property="og:site_name" content="FirstWhistle" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:url" content="https://firstwhistle.app/privacy" />
        <meta name="twitter:title" content="Privacy & Cookies Policy | FirstWhistle" />
        <meta name="twitter:description" content="FirstWhistle's Privacy and Cookie Policy explains how we collect, use, and protect your personal information when you use our football team management application." />
        
        <link rel="canonical" href="https://firstwhistle.app/privacy" />
      </Helmet>

      {/* Main Content Section */}
      <section className="privacy-content-section">
        <div className="privacy-container">
          <div className="privacy-header">
            <h1>Privacy & Cookies Policy</h1>
            <div className="privacy-meta">
              <p><strong>Last Updated:</strong> 27th July 2025</p>
              <p><strong>Version:</strong> 1.0</p>
            </div>
          </div>

          <div className="privacy-content">
            <section className="policy-section">
              <h2>1. Introduction</h2>
              <p>This Privacy and Cookie Policy explains how FirstWhistle ("we", "our", or "us"), operated by Pebble Designs Ltd, collects, uses, and protects your personal information when you use our football team management application.</p>
              <p>We are committed to protecting your privacy and ensuring the security of your personal data in accordance with the UK General Data Protection Regulation (UK GDPR) and other applicable data protection laws.</p>
            </section>

            <section className="policy-section">
              <h2>2. Information We Collect</h2>
              
              <h3>2.1 Information You Provide to Us</h3>
              <ul>
                <li><strong>Account Information:</strong> Name, email address, phone number, date of birth, password</li>
                <li><strong>Team Information:</strong> Team membership, position, shirt number, preferred foot, height</li>
                <li><strong>Performance Data:</strong> Match attendance, goals scored, performance ratings, skill assessments</li>
                <li><strong>Payment Information:</strong> Subscription details, payment history (processed securely through Stripe)</li>
                <li><strong>Communication Preferences:</strong> Notification settings, SMS consent</li>
              </ul>

              <h3>2.2 Information We Collect Automatically</h3>
              <ul>
                <li><strong>Device Information:</strong> Device type, operating system, browser type, IP address</li>
                <li><strong>Usage Data:</strong> App features used, pages visited, time spent in app, interaction patterns</li>
                <li><strong>Location Data:</strong> Match venue locations, training session locations (with your consent)</li>
                <li><strong>Technical Data:</strong> App performance metrics, error logs, crash reports</li>
              </ul>

              <h3>2.3 Information from Third Parties</h3>
              <ul>
                <li><strong>Team Administrators:</strong> Team membership confirmations, match invitations</li>
                <li><strong>Payment Processors:</strong> Payment confirmation and subscription status from Stripe</li>
                <li><strong>SMS Services:</strong> Delivery confirmations from Twilio for notifications</li>
              </ul>
            </section>

            <section className="policy-section">
              <h2>3. How We Use Your Information</h2>
              
              <h3>3.1 Core Service Provision</h3>
              <ul>
                <li><strong>Team Management:</strong> Match scheduling, player attendance tracking, team communications</li>
                <li><strong>Performance Tracking:</strong> Statistics, leaderboards, achievement systems</li>
                <li><strong>Communication:</strong> Match notifications, team announcements, SMS alerts</li>
                <li><strong>Account Management:</strong> User authentication, profile management, subscription services</li>
              </ul>

              <h3>3.2 Service Improvement</h3>
              <ul>
                <li><strong>Analytics:</strong> Understanding app usage patterns to improve functionality</li>
                <li><strong>Performance Monitoring:</strong> Identifying and fixing technical issues</li>
                <li><strong>Feature Development:</strong> Developing new features based on user needs</li>
              </ul>

              <h3>3.3 Legal and Security</h3>
              <ul>
                <li><strong>Compliance:</strong> Meeting legal obligations and regulatory requirements</li>
                <li><strong>Security:</strong> Preventing fraud, abuse, and ensuring platform safety</li>
                <li><strong>Dispute Resolution:</strong> Addressing user complaints and technical issues</li>
              </ul>
            </section>

            <section className="policy-section">
              <h2>4. Legal Basis for Processing</h2>
              <p>We process your personal data based on the following legal grounds:</p>
              <ul>
                <li><strong>Contract Performance:</strong> To provide the football team management services you've requested</li>
                <li><strong>Legitimate Interest:</strong> To improve our services, ensure security, and prevent fraud</li>
                <li><strong>Consent:</strong> For optional features like push notifications and location services</li>
                <li><strong>Legal Obligation:</strong> To comply with applicable laws and regulations</li>
              </ul>
            </section>

            <section className="policy-section">
              <h2>5. Information Sharing and Disclosure</h2>
              
              <h3>5.1 Within Your Team</h3>
              <ul>
                <li><strong>Team Members:</strong> Other players in your team can see your name, position, and match attendance</li>
                <li><strong>Coaches/Managers:</strong> Can access your performance data, attendance records, and contact information</li>
                <li><strong>Team Administrators:</strong> Have access to team-wide information for management purposes</li>
              </ul>

              <h3>5.2 Service Providers</h3>
              <ul>
                <li><strong>Stripe:</strong> Payment processing and subscription management</li>
                <li><strong>Twilio:</strong> SMS notification delivery</li>
                <li><strong>Microsoft Clarity:</strong> Website analytics and user experience monitoring</li>
                <li><strong>Hosting Providers:</strong> Secure data storage and app hosting</li>
              </ul>

              <h3>5.3 Legal Requirements</h3>
              <p>We may disclose your information when required by law, to protect our rights, or to ensure the safety of our users.</p>
            </section>

            <section className="policy-section">
              <h2>6. Data Security</h2>
              <p>We implement appropriate technical and organisational measures to protect your personal data:</p>
              <ul>
                <li><strong>Encryption:</strong> All data transmitted between your device and our servers is encrypted</li>
                <li><strong>Access Controls:</strong> Strict access controls limit who can access your personal data</li>
                <li><strong>Regular Security Audits:</strong> We regularly review and update our security measures</li>
                <li><strong>Secure Development:</strong> We follow secure coding practices and regular security testing</li>
              </ul>
            </section>

            <section className="policy-section">
              <h2>7. Data Retention</h2>
              <p>We retain your personal data for as long as necessary to provide our services:</p>
              <ul>
                <li><strong>Active Accounts:</strong> Data is retained while your account is active</li>
                <li><strong>Inactive Accounts:</strong> Data may be retained for up to 2 years after account deactivation</li>
                <li><strong>Legal Requirements:</strong> Some data may be retained longer to comply with legal obligations</li>
                <li><strong>Deletion:</strong> You can request deletion of your data at any time (see Section 9)</li>
              </ul>
            </section>

            <section className="policy-section">
              <h2>8. Your Rights</h2>
              <p>Under UK GDPR, you have the following rights:</p>
              <ul>
                <li><strong>Access:</strong> Request a copy of your personal data</li>
                <li><strong>Rectification:</strong> Correct inaccurate or incomplete data</li>
                <li><strong>Erasure:</strong> Request deletion of your personal data</li>
                <li><strong>Portability:</strong> Receive your data in a structured, machine-readable format</li>
                <li><strong>Restriction:</strong> Limit how we process your data</li>
                <li><strong>Objection:</strong> Object to certain types of processing</li>
                <li><strong>Withdraw Consent:</strong> Withdraw consent for optional features</li>
              </ul>
              <p>To exercise these rights, contact us at <a href="mailto:privacy@firstwhistle.app">privacy@firstwhistle.app</a></p>
            </section>

            <section className="policy-section">
              <h2>9. Children's Privacy</h2>
              
              <h3>9.1 Age Requirements</h3>
              <ul>
                <li>Users must be at least 13 years old to create an independent account</li>
                <li>Users under 13 may only access the app through a parent or guardian permission</li>
                <li>Parents and guardians have the ability to revoke permission at any point</li>
              </ul>

              <h3>9.2 Parental Controls</h3>
              <ul>
                <li>Parents can manage their children's accounts and data</li>
                <li>Parents must provide consent for their children's data processing</li>
                <li>Children's data is subject to additional protections under UK GDPR</li>
              </ul>

              <h3>9.3 Safeguarding</h3>
              <ul>
                <li>We have specific safeguarding measures for under-18 users</li>
                <li>All users must report inappropriate behaviour or content</li>
                <li>We work with coaches and team administrators to ensure child safety</li>
                <li>We work with individual clubs to align with their specific safeguarding policies and procedures</li>
              </ul>
            </section>

            <section className="policy-section">
              <h2>10. International Data Transfers</h2>
              <p>Your data is primarily processed within the UK and European Economic Area (EEA). When data is transferred outside the EEA, we ensure appropriate safeguards are in place through:</p>
              <ul>
                <li><strong>Adequacy Decisions:</strong> Transfers to countries with adequate data protection laws</li>
                <li><strong>Standard Contractual Clauses:</strong> EU-approved data transfer agreements</li>
                <li><strong>Certification Schemes:</strong> Industry-recognised data protection certifications</li>
              </ul>
            </section>

            <section className="policy-section">
              <h2>11. Cookies and Tracking Technologies</h2>
              
              <h3>11.1 What Are Cookies</h3>
              <p>Cookies are small text files stored on your device that help us provide and improve our services.</p>

              <h3>11.2 Types of Cookies We Use</h3>
              
              <h4>Essential Cookies</h4>
              <ul>
                <li><strong>Authentication:</strong> Maintain your login session and security</li>
                <li><strong>Security:</strong> Protect against fraud and ensure secure data transmission</li>
                <li><strong>Functionality:</strong> Remember your preferences and settings</li>
              </ul>

              <h4>Analytics Cookies</h4>
              <ul>
                <li><strong>Microsoft Clarity:</strong> We use Microsoft Clarity to understand how users interact with our website</li>
                <li><strong>Purpose:</strong> Website analytics, user experience monitoring, performance tracking</li>
                <li><strong>Data Collected:</strong> Page views, user interactions, session recordings, performance metrics</li>
                <li><strong>Retention:</strong> Up to 12 months</li>
                <li><strong>Third Party:</strong> Microsoft Corporation</li>
                <li><strong>Privacy Policy:</strong> <a href="https://privacy.microsoft.com/en-us/privacystatement" target="_blank" rel="noopener noreferrer">Microsoft Privacy Statement</a></li>
              </ul>

              <h4>Performance Cookies</h4>
              <ul>
                <li><strong>Web Vitals:</strong> Monitor app performance and loading times</li>
                <li><strong>Error Tracking:</strong> Identify and fix technical issues</li>
              </ul>

              <h3>11.3 Cookie Management</h3>
              <p>You can control cookies through your browser settings:</p>
              <ul>
                <li><strong>Browser Settings:</strong> Most browsers allow you to block or delete cookies</li>
                <li><strong>Cookie Consent:</strong> We will ask for your consent before setting non-essential cookies</li>
                <li><strong>Opt-out:</strong> You can opt out of analytics cookies while maintaining app functionality</li>
              </ul>

              <h3>11.4 Local Storage</h3>
              <p>We also use browser local storage to:</p>
              <ul>
                <li><strong>Session Management:</strong> Store authentication tokens and user preferences</li>
                <li><strong>Caching:</strong> Improve app performance by caching frequently used data</li>
                <li><strong>Offline Functionality:</strong> Enable basic features when offline</li>
              </ul>
            </section>

            <section className="policy-section">
              <h2>12. Push Notifications</h2>
              
              <h3>12.1 Types of Notifications</h3>
              <ul>
                <li><strong>Match Updates:</strong> Invitations, reminders, and result notifications</li>
                <li><strong>Team Communications:</strong> Announcements from coaches and team administrators</li>
                <li><strong>System Notifications:</strong> App updates and important service information</li>
              </ul>

              <h3>12.2 Notification Management</h3>
              <ul>
                <li><strong>Consent:</strong> You can choose which types of notifications to receive</li>
                <li><strong>Opt-out:</strong> You can disable push notifications at any time</li>
                <li><strong>Settings:</strong> Manage notification preferences in your account settings</li>
              </ul>
            </section>

            <section className="policy-section">
              <h2>13. Changes to This Policy</h2>
              <p>We may update this Privacy and Cookie Policy from time to time. We will notify you of significant changes through:</p>
              <ul>
                <li><strong>In-app Notifications:</strong> Prominent notices within the application</li>
                <li><strong>Email Communications:</strong> Direct emails to registered users</li>
                <li><strong>Website Updates:</strong> Updated policy posted on our website</li>
              </ul>
              <p>Continued use of the app after changes constitutes acceptance of the updated policy.</p>
            </section>

            <section className="policy-section">
              <h2>14. Contact Information</h2>
              <p>For questions about this Privacy and Cookie Policy or to exercise your rights:</p>
              
              <h3>Data Protection Officer</h3>
              <p><strong>Email:</strong> <a href="mailto:support@firstwhistle.app">support@firstwhistle.app</a></p>
              <p><strong>Address:</strong> FYI Media Meadow, Freedom Works, Hastings, East Sussex, TN38 0JG, UK</p>

              <h3>General Privacy Inquiries</h3>
              <p><strong>Email:</strong> <a href="mailto:support@firstwhistle.app">support@firstwhistle.app</a></p>

              <h3>Safeguarding Concerns</h3>
              <p><strong>Email:</strong> <a href="mailto:support@firstwhistle.app">support@firstwhistle.app</a></p>

              <h3>Technical Support</h3>
              <p><strong>Email:</strong> <a href="mailto:support@firstwhistle.app">support@firstwhistle.app</a></p>
            </section>

            <section className="policy-section">
              <h2>15. Complaints</h2>
              <p>If you have concerns about how we handle your personal data, you can:</p>
              <ul>
                <li><strong>Contact Us:</strong> Reach out to our Data Protection Officer</li>
                <li><strong>ICO Complaint:</strong> Lodge a complaint with the Information Commissioner's Office (ICO)</li>
              </ul>
              <p><strong>Website:</strong> <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer">ico.org.uk</a></p>
              <p><strong>Phone:</strong> 0303 123 1113</p>
            </section>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Privacy; 